// Keeps one change-stream connection open for the building in scope, and closes it when the scope
// leaves that building. Mounted once, at the root: views never open streams themselves, they
// subscribe to the refresh signals (building-changes.ts `useBuildingChangeTick`).

import { useEffect } from "react";

import {
  buildingEventStreamUrl,
  issueBuildingEventStreamToken,
} from "@/lib/api/building";
import { getSession } from "@/lib/auth";
import { publishBuildingChange } from "@/lib/building-changes";
import {
  connectBuildingEventStream,
  type EventSourceLike,
} from "@/lib/building-event-stream";
import { useInventoryFilter } from "@/lib/inventory-filter";

function browserEventSource(url: string): EventSourceLike {
  const source = new EventSource(url);
  const like: EventSourceLike = {
    onopen: null,
    onerror: null,
    addEventListener: (type, listener) =>
      source.addEventListener(type, (event) =>
        listener({ lastEventId: (event as MessageEvent).lastEventId }),
      ),
    close: () => source.close(),
  };
  source.onopen = () => like.onopen?.();
  source.onerror = () => like.onerror?.();
  return like;
}

export function useBuildingEventStream(): void {
  const { buildingId } = useInventoryFilter();

  useEffect(() => {
    if (buildingId === null || !getSession()) return;
    return connectBuildingEventStream(buildingId, {
      issueToken: issueBuildingEventStreamToken,
      streamUrl: buildingEventStreamUrl,
      openEventSource: browserEventSource,
      onChange: (kind) => publishBuildingChange(buildingId, kind),
    });
  }, [buildingId]);
}
